<template>
  <div class="row-root" :class="{ locked: !canGather }">
    <div class="row-main">
      <div class="icon-well" aria-hidden="true">{{ skillIcon(node.skillId) }}</div>
      <div class="content">
        <div class="topline">
          <div class="name">{{ node.name }}</div>
          <UiChip variant="muted">
            {{ skillLabel(node.skillId) }}
            <span class="stat-mono num">Lv {{ formatNumber(node.levelReq) }}</span>
          </UiChip>
        </div>
        <div class="subline text-muted">
          <span>Yields {{ node.yieldsItemName }}</span>
          <span class="dot">•</span>
          <span class="stat-mono num">You {{ formatNumber(effectiveLevel) }}</span>
          <span v-if="modifier !== 0" class="stat-mono num">({{ modifier > 0 ? `+${formatNumber(modifier)}` : formatNumber(modifier) }})</span>
          <span class="dot">•</span>
          <span class="stat-mono num">{{ formatNumber(node.xpPerGather) }} XP</span>
        </div>
        <div v-if="blockedReason" class="requirement-line">
          <span aria-hidden="true">{{ blockedReasonIcon }}</span>
          <span>{{ blockedReason }}</span>
        </div>
        <div v-else-if="requiredItemLabel" class="requirement-hint">
          <span aria-hidden="true">{{ requiredItemIcon }}</span>
          <span>{{ requiredItemLabel }}</span>
        </div>
      </div>
    </div>

    <div class="row-actions">
      <UiButton size="sm" tone="work" :disabled="disabled || !canGather" @click="emit('gather', node.id)">
        <template #icon>{{ ICONS.domains.work }}</template>
        {{ canGather ? "Gather" : "Locked" }}
      </UiButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { ResourceNode } from "@idle-rpg/shared";
import { ICONS } from "../../../lib/icons";
import { formatNumber } from "../../../lib/format";
import { skillIcon, skillLabel } from "../../../lib/skillMeta";
import UiButton from "../../ui/UiButton.vue";
import UiChip from "../../ui/UiChip.vue";
import { computed } from "vue";

const props = defineProps<{
  node: ResourceNode;
  effectiveLevel: number;
  modifier: number;
  canGather: boolean;
  blockedReason?: string | null;
  blockedReasonKind?: "busy" | "level" | "item" | null;
  disabled?: boolean;
}>();

const emit = defineEmits<{
  gather: [nodeId: string];
}>();

const requiredItemLabel = computed(() => {
  if (!props.node.requiredItemId) return null;
  const name = props.node.requiredItemName || "item";
  if (props.node.requiredItemMode === "equipped") return `Requires ${name} equipped.`;
  if (props.node.requiredItemMode === "inventory") return `Requires ${name} in inventory.`;
  return `Requires ${name}.`;
});

const requiredItemIcon = computed(() => {
  if (props.node.requiredItemMode === "equipped") return ICONS.itemsByType.tool;
  if (props.node.requiredItemMode === "inventory") return ICONS.itemsByType.bait;
  return ICONS.itemsByType.resource;
});

const blockedReasonIcon = computed(() => {
  if (props.blockedReasonKind === "item") return requiredItemIcon.value;
  return ICONS.states.blocked;
});

</script>

<style scoped>
.row-root {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: stretch;
  gap: 1rem;
  padding: 0.75rem 0.8rem;
  border-radius: var(--radius-2);
  border: 1px solid var(--glass-border-0);
  background: var(--glass-0);
}

.row-root.locked {
  opacity: 0.78;
}

.row-main {
  display: flex;
  gap: 0.8rem;
  align-items: flex-start;
  min-width: 0;
  align-self: start;
}

.icon-well {
  width: 36px;
  height: 36px;
  border-radius: var(--radius-1);
  display: grid;
  place-items: center;
  background: var(--glass-1);
  border: 1px solid var(--glass-border-1);
  flex: 0 0 auto;
}

.content {
  min-width: 0;
  display: grid;
  gap: 0.25rem;
}

.topline {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.name {
  font-weight: 800;
}

.subline {
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.dot {
  opacity: 0.65;
}

.requirement-hint {
  margin-top: 0.25rem;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.85rem;
  color: var(--fg-1);
}

.requirement-line {
  margin-top: 0.25rem;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.85rem;
  color: color-mix(in srgb, var(--state-warning) 82%, var(--fg-1));
}

.row-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  justify-content: flex-end;
  justify-self: end;
  align-self: end;
}

@media (max-width: 720px) {
  .row-root {
    grid-template-columns: 1fr;
  }

  .row-actions {
    justify-content: flex-end;
  }
}
</style>
